import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '../../components/Button';
import { ErrorNotice, Loading } from '../../components/States';
import type { FeedFilter } from '../../lib/api';
import { PostCard } from '../posts/PostCard';
import { useFeed } from './queries';
import styles from './FeedList.module.css';

interface Props {
  filter: FeedFilter;
  empty: ReactNode;
  /** Shown after the last note, when there's nothing older. */
  endMessage?: string;
}

/** Reverse-chronological notes with infinite scroll (and a button fallback). */
export function FeedList({ filter, empty, endMessage = "That's everything on your board." }: Props) {
  const feed = useFeed(filter);
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed;

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (feed.isPending) return <Loading label="Loading notes" />;
  if (feed.isError) return <ErrorNotice error={feed.error} onRetry={() => feed.refetch()} />;

  const posts = feed.data.pages.flat();
  if (posts.length === 0) return <>{empty}</>;

  return (
    <>
      <ul className={styles.list} aria-label="Notes">
        {posts.map((post) => (
          <li key={post.id}>
            <PostCard post={post} />
          </li>
        ))}
      </ul>
      <div ref={sentinel} />
      {hasNextPage ? (
        <div className={styles.more}>
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Show older notes'}
          </Button>
        </div>
      ) : (
        endMessage && <p className={styles.end}>{endMessage}</p>
      )}
    </>
  );
}
